import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { tokens, Tooltip, Icon } from '@hotjar/hotsauce';
import { hasUnlimitedSessions } from '@hotjar/webapp/domains/revenue/plan';
import type { Site } from '@hotjar/site';
import { useHelpHotjarUrl } from '@hotjar/translations';

import { TrafficGaugeSumUp } from './TrafficGaugeSumUp';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${tokens.spacer.x3};
`;

const Title = styled.div`
  ${tokens.text('caption')};
  color: ${tokens.color.text_light};
`;

const TextSection = styled.div`
  ${tokens.text('subtitle2')};
  color: ${tokens.color.text_default};
  display: flex;
  align-items: center;
`;

const DisabledTextSection = styled.div`
  ${tokens.text('subtitle2')};
  color: ${tokens.color.text_disabled};
  display: flex;
  align-items: center;
`;

const ClickableTextSection = styled(TextSection)`
  cursor: pointer;
`;

const GaugeMeasuringState = styled.div`
  background: ${tokens.color.ui_disabled};
  border-radius: 50%;
  height: 80px;
  width: 80px;
`;

interface Props {
  site: Site | undefined;
  isCoverageMeasureComplete: boolean;
  hasPartialMeasure: boolean;
  coveragePercentage: number;
  dailyTraffic: number;
  ccDailyAllowance: number;
  trackClick: (elementName: string, shownTrafficCoverage?: number | null) => void;
  disableAnimation?: boolean;
  isDropDownShown: boolean;
}

const HOTJAR_CAPTURE_TRAFFIC_LINK = '/articles/115011624047';

export const TrafficCoverageSumUp = ({
  site,
  coveragePercentage,
  hasPartialMeasure,
  isCoverageMeasureComplete,
  dailyTraffic,
  ccDailyAllowance,
  trackClick,
  disableAnimation = false,
  isDropDownShown,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const hideTrafficNumber = dailyTraffic === 0 && !isCoverageMeasureComplete;
  const unlimitedSessions = hasUnlimitedSessions(site);

  const handleClickOnInfo = () => {
    let elementName;
    if (isCoverageMeasureComplete && coveragePercentage === 100) {
      elementName = 'full_traffic_coverage_tooltip-learn-more-link';
    } else if (isCoverageMeasureComplete) {
      elementName = 'active-traffic-coverage-rate_tooltip-learn-more-link';
    } else {
      elementName = 'measuring_in_progress_tooltip-learn-more-link';
    }
    trackClick(elementName, coveragePercentage);
    window.open(`${helpHotjarUrl}${HOTJAR_CAPTURE_TRAFFIC_LINK}`, '_blank', 'noopener noreferrer');
  };

  return (
    <Container>
      <div>
        <div>
          <Title>
            {t('common:sampling-widget.tracking-coverage-message.sumup.trafic-title', {
              context: hasPartialMeasure ? 'partial' : 'full',
            })}
          </Title>
          {hideTrafficNumber ? (
            <DisabledTextSection>
              {t('common:sampling-widget.tracking-coverage-message.sumup.measuring')}
            </DisabledTextSection>
          ) : (
            <Tooltip
              text={t('common:sampling-widget.tracking-coverage-message.sumup.trafic-tooltip')}
              placement="left"
              hideOnClick={false}
            >
              <ClickableTextSection onClick={handleClickOnInfo}>
                <div>
                  {dailyTraffic > 1
                    ? t(
                        'common:sampling-widget.tracking-coverage-message.sumup.trafic-body-plural',
                        {
                          sessionNumber: dailyTraffic,
                        },
                      )
                    : t(
                        'common:sampling-widget.tracking-coverage-message.sumup.trafic-body-singular',
                        {
                          sessionNumber: dailyTraffic,
                        },
                      )}
                  &nbsp;&nbsp;
                </div>
                <Icon color="text_light" icon="info_alt" size="m" />
              </ClickableTextSection>
            </Tooltip>
          )}
        </div>
        <Title
          css={css`
            margin-top: ${tokens.spacer.x2};
          `}
        >
          {t('common:sampling-widget.tracking-coverage-message.sumup.coverage-title')}
        </Title>
        <TextSection>
          {unlimitedSessions
            ? t('common:sampling-widget.tracking-coverage-message.sumup.coverage-body-unlimited')
            : t('common:sampling-widget.tracking-coverage-message.sumup.coverage-body', {
                sessionNumber: ccDailyAllowance,
              })}
        </TextSection>
      </div>
      {hideTrafficNumber ? (
        <GaugeMeasuringState />
      ) : (
        <div>
          <TrafficGaugeSumUp
            coveragePercentage={coveragePercentage}
            isCoverageMeasureComplete={hasPartialMeasure || isCoverageMeasureComplete}
            disableAnimation={disableAnimation}
            isDropDownShown={isDropDownShown}
          />
        </div>
      )}
    </Container>
  );
};
